import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { AddressContext } from 'constants/contexts';
import useOverlay from 'hooks/useOverlay';
import { truncate } from 'lodash-es';
import React, { useContext } from 'react';
import Icon from './Icon';
import Skeleton from './Skeleton';
import IconButton from './IconButton';

function AddressList({
  selectedAddress, onSelect, editable, addresses, loading, ...props
}) {
  const { setEditingAddress, deleteAddress, openModal } = useContext(AddressContext);
  const [openOverlay, closeOverlay] = useOverlay();
  function handleDelete(adr) {
    openOverlay({
      title: `Delete Address ${adr.label ? `"${truncate(adr.label, { length: 20 })}"` : ''}`,
      body: 'Are you sure you want to delete this address?',
      onClose: closeOverlay,
      actions: (
        <>
          <Button onClick={closeOverlay}>Cancel</Button>
          <Button
            onClick={() => {
              deleteAddress(adr);
              closeOverlay();
            }}
            variant="muted"
          >
            Yes
          </Button>
        </>
      ),
    });
  }
  if (!addresses) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  if (addresses.length === 0) {
    return (
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'center', py: 5 }}
        gap="10px"
      >
        <Typography variant="body-lg" align="center">No address here!</Typography>
        <Icon name="locationmap-filled" size={64} color="text.disabled" />
      </Stack>
    );
  }
  return (
    <Stack data-testid="address-list" {...props}>
      {
        addresses.map((adr, index) => (
          <Stack
            direction="row"
            sx={{
              cursor: 'pointer',
              textAlign: 'left',
              border: 'none',
              background: 'none',
              backgroundColor: !loading && adr.id && selectedAddress?.id === adr.id ? 'background.elevation2' : undefined,
              borderRadius: {
                xs: 0,
                md: 4,
              },
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'background.elevation1',
              },
            }}
            py={1.5}
            px={2}
            onClick={() => {
              onSelect(adr);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(adr);
              }
            }}
            tabIndex={0}
            gap={2}
            key={adr.id || index}
            data-testid={`address-list-item-${adr.id}`}
          >
            <Icon name={editable && adr.id && selectedAddress?.id === adr.id ? 'location-filled' : 'location-outlined'} size={24} />
            <Box flexGrow={1}>
              <Typography variant="body-md">{adr.label}</Typography>
              <Typography variant="body-caption">{adr.subtitle || adr.text}</Typography>
            </Box>

            {editable ? (
              <Stack direction="row" gap={0.5}>
                <IconButton
                  name="edit-outlined"
                  onClick={() => {
                    setEditingAddress(adr);
                    openModal();
                  }}
                />
                <IconButton
                  name="trash-outlined"
                  onClick={() => handleDelete(adr)}
                />
              </Stack>
            ) : null}
          </Stack>
        ))
      }
    </Stack>
  );
}

function AddressListSkeleton({ loading }) {
  return (
    <Skeleton>
      <AddressList
        editable={false}
        addresses={Skeleton.props([
          { label: 'string', text: ['string', { length: 40 }] },
          { label: 'string', text: ['string', { length: 40 }] },
          { label: 'string', text: ['string', { length: 40 }] },
          { label: 'string', text: ['string', { length: 40 }] },
          { label: 'string', text: ['string', { length: 40 }] },
        ])}
        onSelect={() => { }}
        loading={loading}
      />
    </Skeleton>
  );
}

export default Skeleton.withSkeleton(AddressList, AddressListSkeleton);
